"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import type { Platform } from "@/payload-types";
import { getImageData } from "@/lib/media-utils";
import { PlatformArcCarousel } from "./PlatformArcCarousel";
import { PlatformCarouselAccordion } from "./PlatformCarouselAccordion";
import { GridGroupShell } from "@/payload/blocks/grid-sections/GridGroupShell";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";

const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";

interface PlatformCarouselBlockProps {
  platforms: Platform[];
}

export function PlatformCarouselBlock({
  platforms,
}: PlatformCarouselBlockProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const platform = platforms[activeIndex];
  const n = platforms.length;
  const prevIndex = (activeIndex - 1 + n) % n;
  const nextIndex = (activeIndex + 1) % n;
  const prevPlatform = platforms[prevIndex];
  const nextPlatform = platforms[nextIndex];

  const { url: heroUrl, alt: heroAlt } = getImageData(
    platform.render3d || platform.heroImage,
    platform.name
  );
  const { url: logoUrl } = getImageData(platform.logo, platform.name);

  return (
    <GridGroupShell glowColor="border-white/12">
      {/* ── Row 1: Hero (full span) ── */}
      <section
        className="relative w-full bg-black text-white border-x border-t border-b border-white/15 grid-decorative-verticals"
        style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
      >
        {/* Prev platform — top-left bracket button (desktop only; mobile copy lives inside the arc cell of Row 2) */}
        <button
          onClick={() => setActiveIndex(prevIndex)}
          aria-label={`Previous platform: ${prevPlatform.name}`}
          className="group absolute left-4 top-4 z-40 hidden items-center gap-3 px-5 py-2.5 text-sm text-white/70 transition-colors hover:text-white md:left-6 md:top-6 lg:flex"
        >
          <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
          <span className="text-base leading-none">&lsaquo;</span>
          <span className="hidden sm:inline whitespace-nowrap">
            {prevPlatform.selectorLabel || prevPlatform.name}
          </span>
        </button>

        {/* Next platform — top-right bracket button (desktop only; mobile copy lives inside the arc cell of Row 2) */}
        <button
          onClick={() => setActiveIndex(nextIndex)}
          aria-label={`Next platform: ${nextPlatform.name}`}
          className="group absolute right-4 top-4 z-40 hidden items-center gap-3 px-5 py-2.5 text-sm text-white/70 transition-colors hover:text-white md:right-6 md:top-6 lg:flex"
        >
          <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
          <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
          <span className="hidden sm:inline whitespace-nowrap">
            {nextPlatform.selectorLabel || nextPlatform.name}
          </span>
          <span className="text-base leading-none">&rsaquo;</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="grid-cell lg:border-t-0 col-span-1 aspect-square lg:col-span-3 lg:aspect-[12/5]">
            <AnimatePresence mode="wait">
              <motion.div
                key={platform.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="relative z-30 flex h-full flex-col items-center justify-center px-6 py-6"
              >
                {/* Platform name or logo */}
                <div className="mb-8">
                  {logoUrl ? (
                    <div className="relative mx-auto h-10 w-64 md:h-14 md:w-80">
                      <Image
                        src={logoUrl}
                        alt={platform.name}
                        fill
                        className="object-contain"
                        sizes="320px"
                      />
                    </div>
                  ) : (
                    <h2 className="text-center text-2xl font-subheading md:text-4xl">
                      {platform.name}
                    </h2>
                  )}
                </div>

                {/* Hero image */}
                {heroUrl && (
                  <div className="relative w-full max-w-5xl flex-1 scale-120 translate-y-8">
                    <Image
                      src={heroUrl}
                      alt={heroAlt}
                      fill
                      className="object-contain"
                      sizes="(max-width: 1024px) 100vw, 80vw"
                      priority
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <GridSectionCrosshairs />
      </section>

      {/* ── Row 2: Three columns ── */}
      <section
        className="relative w-full bg-black text-white border-x border-b border-white/15 grid-decorative-verticals"
        style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3">
          {/* Col 1: Description + CTA */}
          <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square order-3 lg:order-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={`desc-${platform.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex h-full flex-col justify-between p-6 lg:p-8"
              >
                <div>
                  {platform.description && (
                    <p className="text-sm text-white/80 whitespace-pre-line">
                      {platform.description}
                    </p>
                  )}
                </div>

                <Link
                  href={`/platforms/${platform.slug}`}
                  className="group mt-6 inline-flex items-center gap-2 self-start border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white hover:text-white"
                >
                  See full specs
                  <span className="inline-block text-lg leading-none transition-transform duration-300 group-hover:rotate-90">
                    +
                  </span>
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Col 2: Arc Carousel (with inline prev/next on mobile) */}
          <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square order-1 lg:order-none">
            <div className="flex h-full flex-col">
              {/* Mobile-only nav buttons: inline above the arc, replace the absolute desktop pair.
                  z-30 lifts the row above the carousel ships (z-10/z-20) so the lifted active ship
                  doesn't intercept clicks on the buttons. */}
              <div className="relative z-30 flex items-center justify-between gap-2 px-4 pt-4 lg:hidden">
                <button
                  onClick={() => setActiveIndex(prevIndex)}
                  aria-label={`Previous platform: ${prevPlatform.name}`}
                  className="group relative flex items-center gap-3 px-4 py-2 text-sm text-white/70 transition-colors hover:text-white"
                >
                  <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
                  <span className="text-base leading-none">&lsaquo;</span>
                  <span className="whitespace-nowrap">
                    {prevPlatform.selectorLabel || prevPlatform.name}
                  </span>
                </button>
                <button
                  onClick={() => setActiveIndex(nextIndex)}
                  aria-label={`Next platform: ${nextPlatform.name}`}
                  className="group relative flex items-center gap-3 px-4 py-2 text-sm text-white/70 transition-colors hover:text-white"
                >
                  <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
                  <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
                  <span className="whitespace-nowrap">
                    {nextPlatform.selectorLabel || nextPlatform.name}
                  </span>
                  <span className="text-base leading-none">&rsaquo;</span>
                </button>
              </div>

              <div className="flex-1">
                <PlatformArcCarousel
                  platforms={platforms}
                  activeIndex={activeIndex}
                  onSelect={setActiveIndex}
                />
              </div>
            </div>
          </div>

          {/* Col 3: Accordion */}
          <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square order-2 lg:order-none">
            <PlatformCarouselAccordion platform={platform} />
          </div>
        </div>
        <GridSectionCrosshairs />
      </section>

      {/* Crawlable index of every platform — the carousel only renders links
          for platforms near the active one, so this guarantees each sub page
          gets a real anchor in the server HTML. Also serves as a screen-reader
          shortcut past the animated selector. */}
      <nav aria-label="All platforms" className="sr-only">
        <ul>
          {platforms.map((p) => (
            <li key={p.id}>
              <Link href={`/platforms/${p.slug}`}>
                {p.name} — full specifications
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </GridGroupShell>
  );
}
